import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useChildren } from '../../hooks/useChildren';
import { useSuggestedCourses } from '../../hooks/useSuggestedCourses';
import { useUnviewedSuggestions } from '../../hooks/useUnviewedSuggestions';
import { useToast } from '../common/Toast';
import { SkeletonPlannerPage } from '../common/Skeleton';
import FadeTransition from '../common/FadeTransition';
import EmptyState from '../common/EmptyState';
import SemesterFilter from './SemesterFilter';
import EmailContentCard from './EmailContentCard';
import CourseCheckboxItem from './CourseCheckboxItem';
import EnrolledCourseCard from './EnrolledCourseCard';
import ExpandableCartCard from './ExpandableCartCard';
import MobileCheckoutBar from './MobileCheckoutBar';
import { Bug, ShoppingCart, CreditCard, CheckCircle, AlertCircle, Mail, BookOpen, Check, DollarSign } from 'lucide-react';
import { SkeletonPlannerCourseItem, SkeletonLine } from '../common/Skeleton';

// Dev mode flag - shows all UI states for testing
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

// Dev mode component to show all planner UI states
function DevModePreview({ toast }) {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  return (
    <div className="border-2 border-dashed border-purple-400 dark:border-purple-600 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Bug className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
          DEV MODE: {isKorean ? '플래너 UI 상태' : 'Planner UI States'}
        </span>
      </div>

      {/* State 1: Loading (Skeleton) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          1. {isKorean ? '로딩 상태 (스켈레톤)' : 'Loading State (Skeleton)'}
        </p>
        <div className="space-y-3">
          <SkeletonPlannerCourseItem />
          <SkeletonPlannerCourseItem />
        </div>
      </div>

      {/* State 2: Empty State */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          2. {isKorean ? '빈 상태' : 'Empty State'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">{isKorean ? '현재 조회 가능한 수업이 없습니다.' : 'No courses available at this time.'}</p>
        </div>
      </div>

      {/* State 3: Semester Filter */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          3. {isKorean ? '학기 필터' : 'Semester Filter'}
        </p>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button className="px-4 py-2 rounded-full text-sm font-medium transition-colors bg-[#589DD5] text-white shrink-0">
            {isKorean ? '2025 봄학기' : 'Spring 2025'}
          </button>
          <button className="px-4 py-2 rounded-full text-sm font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0">
            {isKorean ? '2025 여름학기' : 'Summer 2025'}
          </button>
          <button className="px-4 py-2 rounded-full text-sm font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0">
            {isKorean ? '2025 가을학기' : 'Fall 2025'}
          </button>
        </div>
      </div>

      {/* State 4: Email Content Card */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          4. {isKorean ? '이메일 콘텐츠 카드' : 'Email Content Card'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-3">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#589DD5]/20 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[#589DD5]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  {isKorean ? '2025 봄학기 수업 추천' : 'Course Recommendations for Spring 2025'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isKorean ? '발신: A ONE Academy • 2025년 1월 15일' : 'From: A ONE Academy • January 15, 2025'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State 5: Tabs */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          5. {isKorean ? '수업 탭' : 'Course Tabs'}
        </p>
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          <button className="relative px-4 py-2.5 text-sm font-medium text-[#589DD5]">
            {isKorean ? '추천 (3)' : 'Suggestions (3)'}
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#589DD5] rounded-t-full" />
          </button>
          <button className="px-4 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            {isKorean ? '등록됨 (2)' : 'Enrolled (2)'}
          </button>
        </div>
      </div>

      {/* State 6: Course Checkbox Item (Unselected) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          6. {isKorean ? '수업 카드 - 미선택' : 'Course Card - Unselected'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-transparent hover:border-[#589DD5]/30 transition-all">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#589DD5]" />
                <h3 className="font-medium text-gray-900 dark:text-white text-sm">SAT Math Level 3</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isKorean ? '시작: 2025년 1월 20일 • 12주' : 'Starts: Jan 20, 2025 • 12 weeks'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm font-semibold text-[#589DD5]">$1,200.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State 7: Course Checkbox Item (Selected) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          7. {isKorean ? '수업 카드 - 선택됨' : 'Course Card - Selected'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-[#589DD5] transition-all">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-5 h-5 rounded border-2 bg-[#589DD5] border-[#589DD5] flex items-center justify-center shrink-0">
              <Check className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#589DD5]" />
                <h3 className="font-medium text-gray-900 dark:text-white text-sm">SAT Reading & Writing</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isKorean ? '시작: 2025년 1월 20일 • 12주' : 'Starts: Jan 20, 2025 • 12 weeks'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm font-semibold text-[#589DD5]">$1,200.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State 8: Enrolled Course Card */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          8. {isKorean ? '등록된 수업 카드' : 'Enrolled Course Card'}
        </p>
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 dark:text-white text-sm">AP Calculus BC</h3>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                {isKorean ? '등록됨 • $1,500.00 결제 완료' : 'Enrolled • Paid $1,500.00'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isKorean ? '시작: 2025년 1월 20일' : 'Starts: Jan 20, 2025'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* State 9: Checkout Cart (Desktop) */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          9. {isKorean ? '결제 장바구니 (데스크톱)' : 'Checkout Cart (Desktop)'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 w-80">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#589DD5]/20 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-[#589DD5]" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  {isKorean ? '결제하기' : 'Checkout'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isKorean ? '2개 수업 선택됨' : '2 courses selected'}
                </p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">SAT Math Level 3</span>
                <span className="font-medium text-gray-900 dark:text-white">$1,200</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">SAT Reading</span>
                <span className="font-medium text-gray-900 dark:text-white">$1,200</span>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-4">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-900 dark:text-white">{isKorean ? '합계' : 'Total'}</span>
                <span className="font-bold text-[#589DD5]">$2,400.00</span>
              </div>
            </div>
            <button className="w-full py-3 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
              <CreditCard className="w-4 h-4" />
              {isKorean ? '결제하기' : 'Checkout'}
            </button>
          </div>
        </div>
      </div>

      {/* State 10: Empty Cart */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          10. {isKorean ? '빈 장바구니' : 'Empty Checkout Cart'}
        </p>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 w-80">
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  {isKorean ? '결제하기' : 'Checkout'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isKorean ? '등록할 수업를 선택하세요' : 'Choose courses to enroll'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State 11: Mobile Checkout FAB */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          11. {isKorean ? '모바일 결제 FAB' : 'Mobile Checkout FAB'}
        </p>
        <div className="flex gap-3">
          <div className="bg-[#589DD5] text-white rounded-full px-4 py-3 shadow-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <span className="font-medium">2</span>
            <span className="mx-1">•</span>
            <DollarSign className="w-4 h-4" />
            <span className="font-semibold">2,400</span>
          </div>
        </div>
      </div>

      {/* State 12: Payment Error */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          12. {isKorean ? '결제 오류' : 'Payment Error'}
        </p>
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400">
            {isKorean ? '결제 실패. 다시 시도하거나 고객센터에 문의하세요.' : 'Payment failed. Please try again or contact support.'}
          </p>
        </div>
      </div>

      {/* State 13: Terms Agreement */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          13. {isKorean ? '약관 동의 체크박스' : 'Terms Agreement Checkbox'}
        </p>
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 w-5 h-5 rounded border-2 bg-[#589DD5] border-[#589DD5] flex items-center justify-center shrink-0">
            <Check className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            {isKorean ? (
              <><span className="text-[#589DD5] underline">이용약관 및 환불정책</span>에 동의합니다</>
            ) : (
              <>I agree to the{' '}<span className="text-[#589DD5] underline">Terms & Refund Policy</span></>
            )}
          </span>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
          14. {isKorean ? '토스트 알림' : 'Toast Notifications'}
        </p>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-5">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => toast.success(
                isKorean ? '결제가 완료되었습니다! 수업 등록이 확정되었습니다.' : 'Payment successful! Your course enrollment has been confirmed.',
                {
                  title: isKorean ? '결제 완료' : 'Payment Complete',
                  duration: 6000,
                }
              )}
              className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors"
            >
              {isKorean ? '결제 성공' : 'Payment Success'}
            </button>
            <button
              onClick={() => toast.warning(
                isKorean ? '결제가 취소되었습니다. 선택한 수업은 저장되었습니다.' : 'Payment was cancelled. Your course selections have been saved.',
                {
                  title: isKorean ? '결제 취소됨' : 'Payment Cancelled',
                  duration: 6000,
                }
              )}
              className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
            >
              {isKorean ? '결제 취소' : 'Payment Cancelled'}
            </button>
            <button
              onClick={() => toast.error(
                isKorean ? '결제 실패. 다시 시도하거나 고객센터에 문의하세요.' : 'Payment failed. Please try again or contact support.'
              )}
              className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
            >
              {isKorean ? '결제 오류' : 'Payment Error'}
            </button>
            <button
              onClick={() => toast.success(
                isKorean ? 'Zelle 결제 요청이 제출되었습니다. 사무실에서 확인 후 연락드리겠습니다.' : 'Your Zelle payment request has been submitted. The office will confirm once received.',
                {
                  title: isKorean ? 'Zelle 요청 전송됨' : 'Zelle Request Sent',
                  duration: 6000,
                }
              )}
              className="px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 transition-colors"
            >
              {isKorean ? 'Zelle 토스트' : 'Zelle Toast'}
            </button>
            <button
              onClick={() => toast.success(
                isKorean ? '현금 결제 요청이 제출되었습니다. 사무실로 결제금을 가져와 주세요.' : 'Your cash payment request has been submitted. Please bring payment to the office.',
                {
                  title: isKorean ? '현금 요청 전송됨' : 'Cash Request Sent',
                  duration: 6000,
                }
              )}
              className="px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
            >
              {isKorean ? '현금 토스트' : 'Cash Toast'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// const getChildDisplayName = (child) => {
//   const parts = [];
//   if (child.first_name) parts.push(child.first_name);
//   if (child.korean_name) parts.push(child.korean_name);
//   if (child.last_name) parts.push(child.last_name);
//   return parts.join(' ') || `Student ${child.id}`;
// };

export default function SuggestedCourseList() {
  const { t } = useTranslation('planner');
  const location = useLocation();
  const { selectedChild } = useSelection();
  const { children } = useChildren();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('suggestions');

  // Track if we've applied the initial semester from navigation state
  const hasAppliedInitialSemester = useRef(false);

  // Only fetch data if selected child is valid (belongs to this parent)
  const isValidChild = selectedChild && children.some(c => c.id === selectedChild.id);

  const {
    emails,
    suggestedCourses,
    enrolledCourses,
    semesters,
    loading,
    error,
    selectedSemesterId,
    setSelectedSemesterId,
    selectedCourseIds,
    toggleCourseSelection,
    selectedCoursesDetails,
    totalFees,
    refetch,
  } = useSuggestedCourses(isValidChild ? selectedChild?.id : null);

  const { markAsViewed, countsBySemester } = useUnviewedSuggestions(isValidChild ? selectedChild?.id : null);

  // Apply initial semester from navigation state (from "View Now" popup)
  useEffect(() => {
    const initialSemesterId = location.state?.initialSemesterId;

    // Only apply once, when semesters are loaded and we haven't applied yet
    if (
      initialSemesterId &&
      semesters.length > 0 &&
      !hasAppliedInitialSemester.current
    ) {
      // Verify the semester exists in the list
      const semesterExists = semesters.some(s => s.id === initialSemesterId);
      if (semesterExists) {
        setSelectedSemesterId(initialSemesterId);
      }
      hasAppliedInitialSemester.current = true;
    }
  }, [location.state?.initialSemesterId, semesters, setSelectedSemesterId]);

  // Reset the flag when child changes (new data will be loaded)
  useEffect(() => {
    hasAppliedInitialSemester.current = false;
  }, [selectedChild?.id]);


  if (!selectedChild) {
    return null;
  }

  const hasNoData = suggestedCourses.length === 0 && enrolledCourses.length === 0 && emails.length === 0;

  // Show skeleton during loading OR when we have a child selected but no data fetched yet (initial load)
  const shouldShowSkeleton = loading || (selectedChild && semesters.length === 0 && !error);

  // Loading skeleton
  if (shouldShowSkeleton) {
    return (
      <FadeTransition transitionKey={`${selectedChild?.id}-loading`}>
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <SkeletonPlannerPage />
        </div>
      </FadeTransition>
    );
  }

  // Error state
  if (error) {
    return (
      <FadeTransition transitionKey={`${selectedChild?.id}-error`}>
        <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
          <EmptyState message={error} />
        </div>
      </FadeTransition>
    );
  }

  // Main content - static elements don't re-render on semester change
  return (
    <FadeTransition transitionKey={`${selectedChild?.id}-content`}>
      <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-12 lg:px-20">
        {/* Dev Mode Preview */}
        {DEV_MODE && <DevModePreview toast={toast} />}

        {/* Header - static, doesn't change on semester switch */}
        {/* <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {t('header.coursesFor', { name: getChildDisplayName(selectedChild) })}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('header.subtitle')}
          </p>
        </div> */}

        {/* Empty State */}
        {hasNoData && (
          <>
            <SemesterFilter
              semesters={semesters}
              selectedSemesterId={selectedSemesterId}
              onSemesterChange={setSelectedSemesterId}
              unviewedCounts={countsBySemester}
            />
            <EmptyState message={t('empty.noCoursesAvailable')} />
          </>
        )}

        {!hasNoData && (
          <div className="lg:flex lg:flex-row-reverse lg:gap-6 lg:items-start">
            {/* Expandable Cart Sidebar (Desktop) - static */}
            <div className="hidden lg:block lg:w-80 lg:shrink-0 lg:sticky lg:top-20">
              <ExpandableCartCard
                selectedCoursesDetails={selectedCoursesDetails}
                totalFees={totalFees}
                studentId={selectedChild?.id}
                onPaymentSuccess={refetch}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Semester Filter - static structure, only selection state changes */}
              <SemesterFilter
                semesters={semesters}
                selectedSemesterId={selectedSemesterId}
                onSemesterChange={setSelectedSemesterId}
                unviewedCounts={countsBySemester}
              />

              {/* Semester content - crossfade on semester change */}
              <FadeTransition transitionKey={`semester-${selectedSemesterId}`}>
                {/* Email Content Cards */}
                {emails.map((email) => (
                  <EmailContentCard key={email.id} email={email} onView={markAsViewed} />
                ))}

                {/* Tabs - static structure, counts update without animation */}
                <div className="mb-4">
                  <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
                    {/* Suggestions Tab */}
                    <button
                      onClick={() => setActiveTab('suggestions')}
                      className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeTab === 'suggestions'
                          ? 'text-[#589DD5]'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {t('tabs.suggestions', { count: suggestedCourses.length })}
                      {activeTab === 'suggestions' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#589DD5] rounded-t-full" />
                      )}
                    </button>

                    {/* Enrolled Tab */}
                    <button
                      onClick={() => setActiveTab('enrolled')}
                      className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeTab === 'enrolled'
                          ? 'text-[#589DD5]'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {t('tabs.enrolled', { count: enrolledCourses.length })}
                      {activeTab === 'enrolled' && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#589DD5] rounded-t-full" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Course Lists - only render active tab */}
                {activeTab === 'suggestions' ? (
                  <div className="space-y-3">
                    {suggestedCourses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">{t('empty.noSuggestions')}</p>
                        <p className="text-xs mt-1">{t('empty.noSuggestionsHint')}</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          {t('notices.checkoutDelay')}
                        </p>
                        {suggestedCourses.map((course) => (
                          <CourseCheckboxItem
                            key={course.student_course_id}
                            course={course}
                            isSelected={selectedCourseIds.includes(course.student_course_id)}
                            onToggle={() => toggleCourseSelection(course.student_course_id)}
                            onRefundSuccess={refetch}
                          />
                        ))}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {enrolledCourses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">{t('empty.noEnrolled')}</p>
                        <p className="text-xs mt-1">{t('empty.noEnrolledHint')}</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          {t('notices.enrolledDelay')}
                        </p>
                        {enrolledCourses.map((course) => (
                          <EnrolledCourseCard
                            key={course.student_course_id}
                            course={course}
                            onRefundSuccess={refetch}
                          />
                        ))}
                      </>
                    )}
                  </div>
                )}
              </FadeTransition>
            </div>
          </div>
        )}

        {/* Mobile Floating Checkout FAB - static */}
        {!hasNoData && (
          <MobileCheckoutBar
            selectedCoursesDetails={selectedCoursesDetails}
            totalFees={totalFees}
            studentId={selectedChild?.id}
            onPaymentSuccess={refetch}
          />
        )}
      </div>
    </FadeTransition>
  );
}
